//@flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.ExportingContainer.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//
// </copyright>
// ----------------------------------------------------------------------

import React, { Component, PropTypes } from 'react';
import { findDOMNode } from 'react-dom';
import PrintingUtils from 'share/utils/Izenda.PrintingUtils';
import PrintConstants from 'logic/data/Izenda.PrintConstants';
import resizeDetector from 'share/utils/Izenda.SizeDetector';
import {
  ORIENTATION_VALUE,
  MARGIN_VALUE,
  COLS_LAYOUT,
  MARGIN_KEY,
  MARGIN_INPUT,
  MAX_HEIGHT
} from 'logic/data/Izenda.ExportConstants';
import GridLayout from 'share/components/controls/Izenda.GridLayout';
import PrintablePage from 'share/components/controls/Izenda.PrintablePage';
import Label from 'share/components/controls/Izenda.Label';
import ExportingConfigAction from './Izenda.ExportingConfigAction';
import connect from 'share/enhancers/Izenda.Connect';
import { observer } from 'mobx-react';
import classnames from 'classnames';
import NumericInput from 'share/components/controls/Izenda.NumericInput';
import Validator from 'share/components/hoc/Izenda.Validator';

const LEFT = 'left';
const RIGHT = 'right';
const FORMAT = '#,###.00';
const ValidatorNumbericInput = Validator()(NumericInput);

const MarginInfo = observer((props: any) => {
  const { name, className, position, value: key, model, model: { margins, getMarginSetting } } = props;
  const onChange = val => {
    props.onChange(val, key);
  };

  const max = model.getMaxByKey(key) || MAX_HEIGHT;

  const marginLabel = <Label>{name}</Label>;
  const marginInput = (
    <ValidatorNumbericInput
      disabled={margins !== MARGIN_VALUE.CUSTOM}
      value={getMarginSetting[key]}
      onChange={onChange}
      format={FORMAT}
      field={key}
      containerClassName="margin-config"
      max={max}
      min={0}
    />
  );

  return (
    <div className="u_tableRow">
      <div className={classnames(className, 'u_tableCell')}>
        {position === LEFT ? marginLabel : marginInput}
        {position === RIGHT ? marginLabel : marginInput}
      </div>
    </div>
  );
});

MarginInfo.displayName = 'MarginInfo';

@connect(ExportingConfigAction)
export default class ExportingContainer extends Component {
  static PropTypes = {
    model: PropTypes.object.isRequired
  };

  setPrintablePage = (printablePage: any) => {
    this.printablePage = printablePage;
  };

  getScaleX = () => {
    const printablePageNode = findDOMNode(this.printablePage);
    const dpi = PrintingUtils.getDPI();
    const { model, model: { pageSizeKey, orientation } } = this.props;
    const { widthInInches } = PrintConstants.getPageSize(pageSizeKey, orientation);
    model.scaleX = printablePageNode.getBoundingClientRect().width / (dpi * widthInInches);
  };

  componentDidMount() {
    this.getScaleX();
    const printablePageNode = findDOMNode(this.printablePage);
    resizeDetector.listenTo(printablePageNode, this.getScaleX);

    this.unlisten = () => {
      resizeDetector.removeListener(printablePageNode, this.getScaleX);
    };
  }

  componentWillUnmount() {
    this.unlisten();
    this.unlisten = null;
  }

  render() {
    const {
      changeMarginConfig,
      store: { fieldsStore },
      model,
      model: { orientation, getMarginSetting, centerOnPage }
    } = this.props;
    const marginSetting = orientation === ORIENTATION_VALUE.PORTRAIT ? COLS_LAYOUT.MEDIUM : COLS_LAYOUT.MIN;
    const review = orientation === ORIENTATION_VALUE.PORTRAIT ? COLS_LAYOUT.MEDIUM : COLS_LAYOUT.MAX;

    return (
      <div className="izenda-Wrapper-content u_paddingLeftRight izenda-Exporting">
        <GridLayout className="u_fullHeight u-posRelative">
          <GridLayout.Row className="u_fullHeight u-posRelative">
            <GridLayout.Col cols={marginSetting} className="u_fullHeight u_table u_textRight">
              <MarginInfo
                value={MARGIN_KEY.TOP}
                className="u_alignTop"
                name={MARGIN_INPUT.TOP}
                model={model}
                onChange={changeMarginConfig}
                position={LEFT}
              />
              <MarginInfo
                value={MARGIN_KEY.LEFT}
                className="u_alignMiddle"
                name={MARGIN_INPUT.LEFT}
                model={model}
                onChange={changeMarginConfig}
                position={LEFT}
              />
              <MarginInfo
                value={MARGIN_KEY.BOTTOM}
                className="u_alignBottom"
                name={MARGIN_INPUT.BOTTOM}
                model={model}
                onChange={changeMarginConfig}
                position={LEFT}
              />
            </GridLayout.Col>
            <GridLayout.Col cols={review} className="u_fullHeight u-posRelative u_noPaddingRight u_noPaddingLeft">
              <div className="izenda-Exporting-report">
                <PrintablePage
                  ref={this.setPrintablePage}
                  top={getMarginSetting.top}
                  right={getMarginSetting.right}
                  bottom={getMarginSetting.bottom}
                  left={getMarginSetting.left}
                  header={getMarginSetting.header}
                  footer={getMarginSetting.footer}
                  horizontally={centerOnPage.horizontally}
                  vertically={centerOnPage.vertically}
                  orientation={orientation}
                  store={fieldsStore}
                  exportFormatSetting={model}
                  viewMode={false}
                />
              </div>
            </GridLayout.Col>
            <GridLayout.Col cols={marginSetting} className="u_fullHeight u_table">
              <MarginInfo
                value={MARGIN_KEY.HEADER}
                className="u_alignTop"
                name={MARGIN_INPUT.HEADER}
                model={model}
                onChange={changeMarginConfig}
                position={RIGHT}
              />
              <MarginInfo
                value={MARGIN_KEY.RIGHT}
                className="u_alignMiddle"
                name={MARGIN_INPUT.RIGHT}
                model={model}
                onChange={changeMarginConfig}
                position={RIGHT}
              />
              <MarginInfo
                value={MARGIN_KEY.FOOTER}
                className="u_alignBottom"
                name={MARGIN_INPUT.FOOTER}
                model={model}
                onChange={changeMarginConfig}
                position={RIGHT}
              />
            </GridLayout.Col>
          </GridLayout.Row>
        </GridLayout>
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./ui/modules/ReportDesigner/Exporting/Izenda.ExportingContainer.js